import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { push } from 'connected-react-router'
import { layouts, common } from '@ocean/ui'
import { SessionUtils, ToastUtils } from '@ocean/utils'
import { UserUtils } from 'utils'
import { useLazySelector } from 'hooks'
import Skeleton, { SkeletonVariant } from 'components/common/Skeleton'
import AssessmentPlayerRoutes from 'modules/AssessmentPlayer/routes'
import { routes as requirementsRoutes } from 'modules/requirements/Requirements/routing'

const { PageLayout, PageSegment } = layouts
const { Decorator } = common

const AdaptiveLearningContentPlayer: React.FC = () => {
  const adaptiveLearningContentPlayerRef = useRef<HTMLIFrameElement>(null)
  const { isLoading, error, customerId } = useLazySelector(({ system }) => {
    const { isLoading, result, error } = system.customerInformation
    return {
      isLoading,
      customerId: result?.information.customerId,
      error,
    }
  })
  const dispatch = useDispatch()

  useEffect(() => {
    const exitAdaptiveLearningContentPlayer = (e: MessageEvent) => {
      const { type, sessionId } = e.data
      if (type === 'EXIT_ADAPTIVE_LEARNING_CONTENT_PLAYER') {
        dispatch(push(`${requirementsRoutes.root}?showAdaptiveLearning=true`))
      } else if (type === 'OLP_ADAPTIVE_LEARNING_ASSESSMENT') {
        dispatch(push(`${AssessmentPlayerRoutes.root}`, { sessionId }))
      }
    }
    window.addEventListener('message', exitAdaptiveLearningContentPlayer)
    return () => {
      window.removeEventListener('message', exitAdaptiveLearningContentPlayer)
    }
  }, [dispatch])

  const sendTokenAndUserInfo = () => {
    if (adaptiveLearningContentPlayerRef.current) {
      const sessionToken = SessionUtils.getSessionToken()
      const { connectedPerson, login } = UserUtils.getUserAccountInfo()
      adaptiveLearningContentPlayerRef.current.contentWindow?.postMessage(
        {
          type: 'VIEW_ADAPTIVE_LEARNING_CONTENT',
          sessionToken,
          connectedPerson,
          username: login,
          customerId,
        },
        window.cc.adaptiveLearningContentPlayer ?? ''
      )
      const parentDiv = adaptiveLearningContentPlayerRef.current.parentElement
      if (parentDiv) {
        parentDiv.style.overflow = 'hidden'
      }
    }
  }

  if (isLoading) {
    return <Skeleton variant={SkeletonVariant.Details} />
  }

  if (error) {
    ToastUtils.error(error.error)
    return null
  }

  return (
    <PageLayout>
      <PageSegment segmentId='content'>
        <Decorator root backgroundColor='white' textColor='black'>
          <iframe
            title='Adaptive Learning Content Player'
            ref={adaptiveLearningContentPlayerRef}
            src={window.cc.adaptiveLearningContentPlayer}
            onLoad={sendTokenAndUserInfo}
            width='100%'
            height='100%'
            style={{ border: 'none', overflow: 'auto' }}
            allow='fullscreen'
            role='application'
          />
        </Decorator>
      </PageSegment>
    </PageLayout>
  )
}

export default AdaptiveLearningContentPlayer
